import { z } from "zod";
import { yerelAn } from "./zaman";

/**
 * SUNUCUYA GELEN TARIH-SAAT — TEK YORUM NOKTASI (05.10.2026, UI/UX kaniti "koc-gorusme-ac").
 *
 * ================================================================
 * HATA: 15:00 GIRILEN GORUSME LISTEDE 18:00
 * ================================================================
 * Panelin `datetime-local` alani ve "tarih + saat" alanlari degeri SAAT DILIMI
 * OLMADAN gonderiyor ("2026-10-06T15:00"). Sunucu bunu zorlamali Date ayristirmasiyla
 * okuyordu; JavaScript ofsetsiz tarih-saati CALISTIGI MAKINENIN saat diliminde
 * yorumluyor ve uretim sunucusu UTC: kayit 15:00Z = Istanbul 18:00 oldu. Deneme
 * penceresinde (A3, F-EXM-01a) ayni hata vardi; orada istemci ofsetli gonderiyor
 * ve sunucu ofsetsizi REDDEDIYOR (`denemeAnSemasi`; eski istemcinin iki gun-basi/gun-sonu
 * bicimi haric, bkz. orada). Ayni sinif bircok denetleyicide
 * kaliyordu (gorusme, tanisma planlama, takvim, mesguliyet, ...).
 *
 * IKI SEMA, IKI ANLAM (bagimsiz inceleme 05.10.2026):
 *  - `yerelAnSemasi` — AN (zaman damgasi sutunu): ofsetsiz "YYYY-AA-GGTSS:DD[:ss[.sss]]"
 *    Europe/Istanbul DUVAR SAATI (`yerelAn`, `Intl` ile — sabit +3 degil); ofsetli/`Z`
 *    aynen. YALNIZ TARIH eskisi gibi UTC gece yarisi.
 *  - `gunSemasi` — GUN (`@db.Date` ya da "yerel gunun UTC gece yarisi" gosterimi): yalniz
 *    "YYYY-AA-GG" ya da tam UTC gece yarisi ISO'su ("…T00:00:00(.000)Z"). Saatli deger
 *    REDDEDILIR: GUN sutununa ofsetsiz "…T00:30" Istanbul olarak cevrilseydi bir onceki
 *    gune yazilirdi.
 * KATI AYRISTIRMA: desene uymayan ya da aralik disi deger ("T25:00", "T10:99",
 * "2026-13-45", boslukla ayrilmis "2026-10-06 15:00", dakikasiz "T15", 4+ haneli ms)
 * sessizce baska bir ana TASINMAZ, reddedilir. (Eski zorlamali ayristirma bunlarin
 * bir kismini reddediyordu; yeni kural gerilemesin.)
 *
 * Neden istemcide degil: kural sunucuda (API-first) — panel, ogrenci uygulamasi ve
 * ileride mobil ayni ucu cagiriyor; her istemcinin cevirmeyi hatirlamasi gerekmesin.
 * Bekci (`an-semasi-bekci.test.ts`): apps/api/src'de zorlamali tarih ayristirmasi yok;
 * denetleyicide sorgu dizgisi `new Date(degisken)` ile cozulmez.
 */
const SADECE_GUN = /^(\d{4})-(\d{2})-(\d{2})$/;
const OFSETSIZ_TARIH_SAAT =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/;
const OFSETLI_TARIH_SAAT =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?(Z|[+-]\d{2}:?\d{2})$/;
const GECERSIZ = (): Date => new Date(Number.NaN);

/** Takvimde var olan gun mu (2026-02-30, 2026-13-01 hayir). */
function gecerliGun(y: number, a: number, g: number): boolean {
  if (a < 1 || a > 12 || g < 1 || g > 31) return false;
  const d = new Date(Date.UTC(y, a - 1, g));
  return d.getUTCFullYear() === y && d.getUTCMonth() === a - 1 && d.getUTCDate() === g;
}

function gecerliSaat(s: number, d: number, sn: number): boolean {
  return s >= 0 && s <= 23 && d >= 0 && d <= 59 && sn >= 0 && sn <= 59;
}

const msDegeri = (ms: string | undefined): number => Number((ms ?? "0").padEnd(3, "0"));

/**
 * Ofsetsiz tarih-saati Istanbul duvar saati olarak cozer. Desene uymazsa `null`; desene
 * uyup aralik disiysa GECERSIZ Date (cagiran reddeder).
 */
export function ofsetsizYerelAn(deger: string): Date | null {
  const m = OFSETSIZ_TARIH_SAAT.exec(deger.trim());
  if (!m) return null;
  const [y, a, g, s, d, sn] = [m[1], m[2], m[3], m[4], m[5], m[6] ?? "0"].map(Number) as [
    number,
    number,
    number,
    number,
    number,
    number,
  ];
  if (!gecerliGun(y, a, g) || !gecerliSaat(s, d, sn)) return GECERSIZ();
  const an = yerelAn(`${m[1]}-${m[2]}-${m[3]}`, `${m[4]}:${m[5]}`);
  return new Date(an.getTime() + sn * 1000 + msDegeri(m[7]));
}

/**
 * AN dizgisini cozer (sorgu parametreleri ve `yerelAnSemasi` ayni fonksiyondan gecer).
 * Kabul edilmeyen bicim ya da aralik disi deger GECERSIZ Date doner.
 */
export function yerelAnCoz(ham: string): Date {
  const deger = ham.trim();
  const gun = SADECE_GUN.exec(deger);
  if (gun) {
    const [y, a, g] = [gun[1], gun[2], gun[3]].map(Number) as [number, number, number];
    return gecerliGun(y, a, g) ? new Date(`${deger}T00:00:00.000Z`) : GECERSIZ();
  }
  const ofsetsiz = ofsetsizYerelAn(deger);
  if (ofsetsiz) return ofsetsiz;
  const o = OFSETLI_TARIH_SAAT.exec(deger);
  if (!o) return GECERSIZ();
  const [y, a, g, s, d, sn] = [o[1], o[2], o[3], o[4], o[5], o[6] ?? "0"].map(Number) as [
    number,
    number,
    number,
    number,
    number,
    number,
  ];
  if (!gecerliGun(y, a, g) || !gecerliSaat(s, d, sn)) return GECERSIZ();
  return new Date(deger);
}

/** GUN dizgisini cozer: "YYYY-AA-GG" ya da tam UTC gece yarisi ISO'su; gerisi GECERSIZ. */
export function gunCoz(ham: string): Date {
  const deger = ham.trim();
  const gun = SADECE_GUN.exec(deger);
  const tam = gun ? null : /^(\d{4}-\d{2}-\d{2})T00:00(?::00(?:\.0{1,3})?)?Z$/.exec(deger);
  const metin = gun ? deger : tam?.[1];
  if (!metin) return GECERSIZ();
  const [y, a, g] = metin.split("-").map(Number) as [number, number, number];
  return gecerliGun(y, a, g) ? new Date(`${metin}T00:00:00.000Z`) : GECERSIZ();
}

/*
  TIP NOTU (tsc, 05.10.2026): denetleyicilerin yerel `dogrula<T>(sema: z.ZodType<T>)`
  yardimcisi girdi = cikti varsayiyor; eski zorlamali ayristirma da girdiyi `Date` diye
  BILDIRIYORDU (calisma aninda dizgi kabul ederken). Ayni sozlesme korunuyor: semalar
  disariya `z.ZodType<Date>` olarak verilir. Calisma aninda girdi `string | Date | number`.
*/
const gecerliMi = (d: Date): boolean => !Number.isNaN(d.getTime());

const anDonusumu = z.union([z.string(), z.date(), z.number()]).transform((v, ctx) => {
  const d = typeof v === "string" ? yerelAnCoz(v) : new Date(v);
  if (gecerliMi(d)) return d;
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: "Tarih-saat geçersiz ya da desteklenmeyen biçimde.",
  });
  return z.NEVER;
});

const gunDonusumu = z.union([z.string(), z.date()]).transform((v, ctx) => {
  const d =
    typeof v === "string" ? gunCoz(v) : v.toISOString().endsWith("T00:00:00.000Z") ? v : GECERSIZ();
  if (gecerliMi(d)) return d;
  ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Tarih YYYY-AA-GG biçiminde olmalı." });
  return z.NEVER;
});

/** AN sutunlari icin (zorlamali Date ayristirmasinin yerine). Date ve sayi aynen gecer. */
export const yerelAnSemasi = anDonusumu as unknown as z.ZodType<Date>;

/** GUN sutunlari icin. Date nesnesi yalnizca tam UTC gece yarisiysa kabul edilir. */
export const gunSemasi = gunDonusumu as unknown as z.ZodType<Date>;
